import React, { useState } from 'react';

import { ChevronDown, ChevronRight } from 'lucide-react';
import { DateTime } from 'luxon';
import { queryCache, useMutation, useQuery } from 'react-query';
import { Toast } from 'src/common/components';

import { fetchCadenceConfig, setCadenceDays } from '../api';
import { CADENCE_QUERY_KEY } from '../constants';
import {
  CATEGORY_COLOR,
  CATEGORY_LABEL,
  COHORT_ATTRIBUTE_LABEL,
  EDITION_AUDIENCE_LABEL,
  RENDER_SLOT_LABEL,
} from '../constants';
import { CadenceAudienceConfig } from '../types';
import { CohortAttribute, FeatureCategory } from '../types';

const CadenceRow: React.FC<{ config: CadenceAudienceConfig }> = ({ config }) => {
  const [days, setDays] = useState(String(config.interval_days));
  const [doSave, { isLoading }] = useMutation(
    (value: number) => setCadenceDays(config.audience, value),
    {
      onSuccess: (res) => {
        void queryCache.invalidateQueries(CADENCE_QUERY_KEY);
        setDays(String(res.data.interval_days));
        Toast.success('Cadencia actualizada.');
      },
      onError: () => {
        Toast.error('No se pudo guardar la cadencia.');
      },
    }
  );
  const dirty = String(config.interval_days) !== days;
  const valid = /^[0-9]+$/.test(days) && Number(days) > 0 && Number(days) <= 36500;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: '#F3F6FA',
        borderRadius: 10,
        border: '1px solid #E1E7F3',
      }}
    >
      <strong style={{ minWidth: 70 }}>{EDITION_AUDIENCE_LABEL[config.audience]}</strong>
      <span>Cada</span>
      <input
        type="number"
        min={1}
        value={days}
        onChange={(e) => setDays(e.target.value)}
        style={{
          width: 70,
          padding: '4px 8px',
          border: '1px solid #D8DADF',
          borderRadius: 6,
          fontSize: 13,
        }}
      />
      <span>días</span>
      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6B7280' }}>
        Próximo ciclo:{' '}
        {config.next_cycle_date
          ? DateTime.fromISO(config.next_cycle_date).toLocaleString(DateTime.DATE_MED)
          : '—'}
      </span>
      <button
        type="button"
        className="btn btn-primary"
        style={{ padding: '5px 14px', fontSize: 13 }}
        disabled={!dirty || !valid || isLoading}
        onClick={() => doSave(Number(days))}
      >
        Guardar
      </button>
    </div>
  );
};

const CadenceSection: React.FC = () => {
  const { data, isLoading } = useQuery([CADENCE_QUERY_KEY], fetchCadenceConfig);
  if (isLoading) return <p>Cargando cadencia…</p>;
  const configs = data?.data.audiences ?? [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ marginBottom: 4 }}>
        Cada audiencia corre con su propio calendario. Staff va a usuarios activos que no son
        gestores; Admins va solo a los gestores de cada cuenta.
      </p>
      {configs.map((c) => (
        <CadenceRow key={c.audience} config={c} />
      ))}
    </div>
  );
};

type Section = {
  id: string;
  title: string;
  body: React.ReactNode;
};

const Card: React.FC<{ title: string; body: React.ReactNode }> = ({ title, body }) => {
  const [open, setOpen] = useState(true);
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #D8DADF',
        borderRadius: 14,
        boxShadow: '0 1px 2px rgba(17,26,43,.06)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          borderBottom: open ? '1px solid #E1E7F3' : 0,
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ font: "700 15px/1.3 'Montserrat',sans-serif", color: '#2E4774' }}>
          {title}
        </span>
        {open ? (
          <ChevronDown size={16} color="#6B7280" />
        ) : (
          <ChevronRight size={16} color="#6B7280" />
        )}
      </button>
      {open && (
        <div style={{ padding: '18px 20px', fontSize: 14, lineHeight: 1.65, color: '#3D4148' }}>
          {body}
        </div>
      )}
    </div>
  );
};

const Tag: React.FC<{ label: string; color?: string }> = ({ label, color = '#2E4774' }) => (
  <span
    style={{
      display: 'inline-block',
      fontSize: 12,
      fontWeight: 600,
      color: '#fff',
      background: color,
      padding: '4px 10px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
      margin: '2px 3px',
    }}
  >
    {label}
  </span>
);

const ConfigTab: React.FC = () => {
  const sections: Section[] = [
    {
      id: 'cadencia',
      title: 'Cadencia de envíos por audiencia',
      body: <CadenceSection />,
    },
    {
      id: 'flujo',
      title: '¿Cómo funciona el flujo completo?',
      body: (
        <ol
          style={{ paddingLeft: 20, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          <li>
            <strong>PR scan diario</strong> — El motor revisa los pull requests fusionados en los
            repositorios <code>cosmos</code>, <code>telescope</code> y <code>rocket</code> en las
            últimas 24 h y clasifica cada PR con Claude (Anthropic API). Solo los marcados como
            "customer-facing" avanzan.
          </li>
          <li>
            <strong>FeatureCatalogEntry</strong> — Cada PR customer-facing se convierte en una
            entrada del catálogo con título (en español), descripción interna y beneficio para el
            cliente. Cualquier entrada puede agregarse a una edición; lo que controla el envío es la
            aprobación de la edición.
          </li>
          <li>
            <strong>Auto-build de edición</strong> — Si no hay una edición en preparación, el motor
            puede crear una automáticamente: selecciona las funcionalidades más recientes y
            relevantes, las asigna a slots (Hero, Card, Conoce Más) y deja el email en borrador
            listo para revisión.
          </li>
          <li>
            <strong>Revisión del staff</strong> — En la pestaña <em>Próxima edición</em> se revisan
            los candidatos, se ajusta el texto, se define la cohorte y se envía una prueba antes de
            aprobar.
          </li>
          <li>
            <strong>Notificación en Slack</strong> — 48 h antes del envío programado, llega un
            mensaje en <strong>#product-digest</strong> recordando revisar la edición.
          </li>
          <li>
            <strong>Envío</strong> — Al marcar como "lista para enviar" y confirmar, SendGrid
            despacha el email a la cohorte seleccionada.
          </li>
        </ol>
      ),
    },
    {
      id: 'slots',
      title: 'Slots del email (Hero / Card / Conoce Más)',
      body: (
        <div>
          <p style={{ marginBottom: 14 }}>
            Cada edición tiene tres zonas visuales. Cada candidato se asigna a uno de estos slots:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {Object.entries(RENDER_SLOT_LABEL).map(([key, label]) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  padding: '12px 14px',
                  background: '#F3F6FA',
                  borderRadius: 10,
                  border: '1px solid #E1E7F3',
                }}
              >
                <Tag label={label} />
                <div>
                  {key === 'hero' && (
                    <p style={{ margin: 0 }}>
                      Portada del email. Máximo <strong>2</strong> candidatos. Reservado para las
                      funcionalidades de mayor impacto (nuevas capacidades AI, integraciones clave).
                      Lleva imagen y CTA prominente.
                    </p>
                  )}
                  {key === 'card' && (
                    <p style={{ margin: 0 }}>
                      Sección de funcionalidades secundarias. Máximo <strong>4</strong> candidatos.
                      Cada tarjeta tiene título, breve descripción y CTA. Ideal para mejoras de
                      flujo y eficiencia.
                    </p>
                  )}
                  {key === 'learn_more' && (
                    <p style={{ margin: 0 }}>
                      Sección de enlaces al pie del email. Máximo <strong>3</strong> candidatos.
                      Para funcionalidades menores, correcciones de usabilidad o mejoras en
                      citas.pr.
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'categorias',
      title: 'Categorías de funcionalidades',
      body: (
        <div>
          <p style={{ marginBottom: 14 }}>
            Cada candidato pertenece a una categoría. Sirve para filtrar el catálogo y para colorear
            las tarjetas en el email.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(Object.entries(CATEGORY_LABEL) as [FeatureCategory, string][]).map(([key, label]) => (
              <Tag key={key} label={label} color={CATEGORY_COLOR[key]} />
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'cohortes',
      title: 'Cohortes de destinatarios',
      body: (
        <div>
          <p style={{ marginBottom: 14 }}>Cada edición se envía a una cohorte. Hay tres modos:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div
              style={{
                padding: '10px 14px',
                background: '#F3F6FA',
                borderRadius: 10,
                border: '1px solid #E1E7F3',
              }}
            >
              <strong>Todas las cuentas</strong> — Se envía a todas las cuentas activas que tengan
              al menos un usuario con email verificado y notificaciones de producto activadas.
            </div>
            <div
              style={{
                padding: '10px 14px',
                background: '#F3F6FA',
                borderRadius: 10,
                border: '1px solid #E1E7F3',
              }}
            >
              <strong>Por atributo (automático)</strong> — Se filtra por una característica de la
              cuenta. Atributos disponibles:
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(Object.entries(COHORT_ATTRIBUTE_LABEL) as [CohortAttribute, string][]).map(
                  ([k, l]) => (
                    <Tag key={k} label={l} color="#374151" />
                  )
                )}
              </div>
              <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, color: '#6B7280' }}>
                Usar atributo cuando el email contiene funcionalidades específicas a un segmento
                (ej.: edición de Fila Digital solo a cuentas con lista de espera v2 activa).
              </p>
            </div>
            <div
              style={{
                padding: '10px 14px',
                background: '#F3F6FA',
                borderRadius: 10,
                border: '1px solid #E1E7F3',
              }}
            >
              <strong>Manual</strong> — Se seleccionan cuentas individuales por nombre. Útil para
              pruebas o lanzamientos graduales.
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'env',
      title: 'Variables de entorno necesarias',
      body: (
        <div>
          <p style={{ marginBottom: 14 }}>
            El motor requiere las siguientes variables configuradas en el servidor:
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F3F6FA', textAlign: 'left' }}>
                <th
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid #E1E7F3',
                    fontWeight: 700,
                  }}
                >
                  Variable
                </th>
                <th
                  style={{
                    padding: '8px 12px',
                    borderBottom: '1px solid #E1E7F3',
                    fontWeight: 700,
                  }}
                >
                  Propósito
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                ['ANTHROPIC_API_KEY', 'Clasificar PRs con Claude (claude-haiku-4-5-20251001)'],
                ['GITHUB_TOKEN', 'Leer PR descriptions de los repos cosmos / telescope / rocket'],
                ['SLACK_BOT_TOKEN', 'Enviar notificaciones a #product-digest'],
                ['PRODUCT_DIGEST_SLACK_CHANNEL_ID', 'ID del canal de Slack (#product-digest)'],
                ['SLACK_MESSAGING_ENABLED', 'True para activar envíos reales (default: False)'],
                ['SENDGRID_API_KEY', 'Enviar emails a los destinatarios'],
                ['ADMIN_PORTAL_URL', 'URL de este portal (para links en emails y Slack)'],
              ].map(([k, v]) => (
                <tr key={k} style={{ borderBottom: '1px solid #F0F0F0' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <code
                      style={{
                        fontSize: 12,
                        background: '#EBF1FA',
                        padding: '2px 6px',
                        borderRadius: 4,
                        color: '#2E4774',
                      }}
                    >
                      {k}
                    </code>
                  </td>
                  <td style={{ padding: '8px 12px', color: '#545964' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
    {
      id: 'repos',
      title: 'Repositorios escaneados',
      body: (
        <div>
          <p style={{ marginBottom: 10 }}>
            El scan cubre los siguientes repos de GitHub bajo la org <strong>brainhi</strong>:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              ['cosmos', 'Backend Django — APIs, lógica de negocio, Celery tasks'],
              ['telescope', 'Frontend React — panel del staff (orbit), portal del cliente'],
              ['rocket', 'Servicios auxiliares y microservicios'],
            ].map(([repo, desc]) => (
              <div
                key={repo}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: '#F3F6FA',
                  borderRadius: 10,
                  border: '1px solid #E1E7F3',
                }}
              >
                <code style={{ fontSize: 13, fontWeight: 700, color: '#2E4774', minWidth: 80 }}>
                  {repo}
                </code>
                <span style={{ fontSize: 13, color: '#545964' }}>{desc}</span>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 14, fontSize: 13, color: '#6B7280' }}>
            Para agregar un repo nuevo, modificar la lista en{' '}
            <code>cosmos/product_updates/tasks.py</code> (constante <code>SCAN_REPOS</code>).
          </p>
        </div>
      ),
    },
    {
      id: 'celery',
      title: 'Tareas Celery programadas',
      body: (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              {
                task: 'scan_pull_requests_for_product_updates',
                schedule: 'Diario · 8:00 AM UTC',
                desc: 'Escanea PRs fusionados en las últimas 24 h y crea FeatureCatalogEntries.',
              },
              {
                task: 'notify_editions_pending_review',
                schedule: 'Diario · 9:00 AM UTC',
                desc: 'Envía mensaje en Slack si hay una edición APPROVED con envío programado en menos de 48 h.',
              },
            ].map((t) => (
              <div
                key={t.task}
                style={{
                  padding: '12px 14px',
                  background: '#F3F6FA',
                  borderRadius: 10,
                  border: '1px solid #E1E7F3',
                }}
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                  <code style={{ fontSize: 12, fontWeight: 700, color: '#2E4774' }}>{t.task}</code>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      background: '#EBF1FA',
                      color: '#2E4774',
                      border: '1px solid #D6E2F3',
                      padding: '2px 8px',
                      borderRadius: 999,
                    }}
                  >
                    {t.schedule}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#545964' }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full overflow-y-auto" style={{ background: '#F8FAFD' }}>
      <div style={{ maxWidth: 860, width: '100%', margin: '0 auto', padding: '28px 24px 48px' }}>
        <div style={{ marginBottom: 24 }}>
          <h2
            style={{
              font: "800 22px/1.2 'Montserrat',sans-serif",
              color: '#111A2B',
              marginBottom: 6,
            }}
          >
            Referencia de configuracion
          </h2>
          <p style={{ fontSize: 14.5, color: '#6B7280', margin: 0 }}>
            Documentacion para el equipo de BrainHi sobre como funciona y se configura el motor de
            Product Updates.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {sections.map((s) => (
            <Card key={s.id} title={s.title} body={s.body} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConfigTab;
